import { WebStorageStateStore } from "oidc-client-ts";

import type { AuthProviderProps } from "react-oidc-context";

/**
 * Whether the auth gate is enabled. Controlled by VITE_APP_AUTH_ENABLED so
 * local development can run the app without a Cognito user pool.
 */
export const AUTH_ENABLED = import.meta.env.VITE_APP_AUTH_ENABLED === "true";

const authority = import.meta.env.VITE_APP_COGNITO_AUTHORITY;
const clientId = import.meta.env.VITE_APP_COGNITO_CLIENT_ID;
const cognitoDomain = import.meta.env.VITE_APP_COGNITO_DOMAIN;
const scope = import.meta.env.VITE_APP_COGNITO_SCOPE || "openid email profile";

/**
 * The redirect URI registered on the Cognito app client. Falls back to the
 * current origin so the same build works across environments as long as the
 * origin is registered as a callback URL in Cognito.
 */
const redirectUri =
  import.meta.env.VITE_APP_COGNITO_REDIRECT_URI || window.location.origin;

/**
 * react-oidc-context / oidc-client-ts configuration for the Cognito user pool.
 *
 * Cognito is an OIDC provider, so the library auto-discovers the authorize,
 * token, userInfo, and JWKS endpoints from `${authority}/.well-known/
 * openid-configuration`. We use the Authorization Code flow with PKCE
 * (response_type=code), which oidc-client-ts performs with S256 by default —
 * the only method Cognito accepts for public clients.
 */
export const oidcConfig: AuthProviderProps = {
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  response_type: "code",
  scope,
  // Persist tokens across reloads/tabs.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Renew the access token silently before it expires.
  automaticSilentRenew: true,
  // After the redirect callback, restore the path the user was on before
  // sign-in (e.g. a /s/<id> share link), which we stashed in the OIDC `state`.
  // Falls back to the root path. This also strips the ?code & ?state params.
  onSigninCallback: (user?: { state?: unknown } | void) => {
    const returnTo =
      (user &&
        typeof user.state === "object" &&
        user.state !== null &&
        typeof (user.state as { returnTo?: unknown }).returnTo === "string" &&
        (user.state as { returnTo: string }).returnTo) ||
      window.location.pathname;
    window.history.replaceState({}, document.title, returnTo);
  },
};

/**
 * Build the Cognito managed-login logout URL. Clearing local tokens alone
 * leaves the Cognito (and upstream Entra) session intact, so a full sign-out
 * must redirect through Cognito's /logout endpoint.
 */
export const buildCognitoLogoutUrl = (): string => {
  const logoutRedirect = redirectUri;
  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: logoutRedirect,
  });
  return `${cognitoDomain}/logout?${params.toString()}`;
};

/**
 * True when the required Cognito config is present. Lets us fail loudly with a
 * helpful message instead of bouncing to a broken authorize URL.
 */
export const isAuthConfigured = Boolean(authority && clientId && cognitoDomain);
